import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, Clock, AlertTriangle, Info } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

const DriverMessages = () => {
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newMessage, setNewMessage] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('NORMAL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMessages();
    // Auto-refresh every 15 seconds for real-time messaging
    const interval = setInterval(fetchMessages, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axiosInstance.get('/driver/messages');
      if (response.data.success) {
        setMessages(response.data.data.messages);
        setUnreadCount(response.data.data.unreadCount);
        setError(null);
      } else {
        setError('Fehler beim Laden der Nachrichten: ' + (response.data.message || 'Unbekannter Fehler'));
      }
    } catch (err) {
      setError('Fehler beim Laden der Nachrichten: ' + err.message);
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await axiosInstance.post('/driver/messages', {
        message: newMessage.trim(),
        priority: selectedPriority
      });
      
      if (response.data.success) {
        setNewMessage('');
        setSelectedPriority('NORMAL');
        await fetchMessages(); // Refresh messages
      }
    } catch (err) {
      setError('Fehler beim Senden der Nachricht');
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'HIGH': return <AlertTriangle className="text-red-500" size={16} />;
      case 'NORMAL': return <Info className="text-blue-500" size={16} />;
      default: return <Info className="text-gray-500" size={16} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH': return 'border-l-red-500 bg-red-50';
      case 'NORMAL': return 'border-l-blue-500 bg-blue-50';
      default: return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'HIGH': return 'Hoch';
      case 'NORMAL': return 'Normal';
      default: return 'Unbekannt';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-100 h-20 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          <MessageCircle className="mr-2 text-blue-600" size={24} />
          Fahrer Nachrichten
          {unreadCount > 0 && (
            <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
              {unreadCount} neu
            </span>
          )}
        </h2>
        <button
          onClick={fetchMessages}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Aktualisieren
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Send Message Form */}
      <form onSubmit={sendMessage} className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-medium text-gray-800 mb-4">Neue Nachricht senden</h3>
        
        <div className="flex flex-col space-y-4">
          <div className="flex items-center space-x-4">
            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="NORMAL">Normal</option>
              <option value="HIGH">Hoch</option>
            </select>
          </div>
          
          <div className="flex space-x-4">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Nachricht an alle Fahrer..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={200}
            />
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Senden
                </>
              )}
            </button>
          </div>
          
          <div className="text-sm text-gray-600">
            {newMessage.length}/200 Zeichen
          </div>
        </div>
      </form>

      {/* Messages List */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-800">Aktuelle Nachrichten</h3>
        
        {messages.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageCircle size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg">Keine Nachrichten vorhanden</p>
            <p className="text-sm">Nachrichten von anderen Fahrern werden hier angezeigt</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`border-l-4 p-4 rounded-lg ${getPriorityColor(message.priority)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    {getPriorityIcon(message.priority)}
                    <span className="font-medium text-gray-800">
                      {message.sender}
                    </span>
                    <span className="text-xs px-2 py-1 bg-white rounded-full text-gray-600">
                      {getPriorityText(message.priority)}
                    </span>
                  </div>
                  
                  <p className="text-gray-700 mb-2">{message.message}</p>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock size={14} className="mr-1" />
                    vor {message.timeAgo}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Message Guidelines */}
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h4 className="font-medium text-yellow-800 mb-2">📋 Nachrichtenguidelines</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• Teile wichtige Verkehrsinformationen mit anderen Fahrern</li>
          <li>• Verwende "Hoch" Priorität nur für dringende Meldungen</li>
          <li>• Sei respektvoll und konstruktiv in deinen Nachrichten</li>
          <li>• Melde Hotspots und gute Fahrgelegenheiten</li>
        </ul>
      </div>
    </div>
  );
};

export default DriverMessages;
